import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { store } from './store';
import App from './App';
import AuthGate from './components/Auth/AuthGate';
import { AuthProvider } from './hooks/useAuth';
import i18n, { getDirection, getSavedLanguage } from './translations';
import './index.css';
import './styles/landing-typography.css';

// Lazy-load the public chart page - keeps it out of the main bundle since
// only unauthenticated visitors arriving on /public/chart/<ticker> reach it.
const PublicChart = React.lazy(() => import('./pages/PublicChart'));
const AuthActionCompletePage = React.lazy(
  () => import('./components/Auth/AuthActionCompletePage')
);

// Initialize i18n and sync document direction BEFORE first render
// This prevents flash of wrong direction for Hebrew users
const savedLanguage = getSavedLanguage();
const direction = getDirection(savedLanguage);
document.documentElement.lang = savedLanguage;
document.documentElement.dir = direction;

// Load the correct Bootstrap CSS (LTR or RTL) based on saved direction.
// Conditional require() ensures only the right stylesheet is bundled on initial load.
if (direction === 'rtl') {
  require('bootstrap/dist/css/bootstrap.rtl.min.css');
} else {
  require('bootstrap/dist/css/bootstrap.min.css');
}

// When language direction changes (e.g., English→Hebrew), reload to swap Bootstrap CSS.
// style-loader can't unload previously injected CSS, so a reload is the clean path.
i18n.on('languageChanged', (lng) => {
  const newDir = getDirection(lng);
  if (document.documentElement.dir !== newDir) {
    window.location.reload();
  }
});

// Apply TradingView theme to body
document.body.classList.add('tradingview-theme');

// Ignore noisy extension-originated promise errors in local development.
// These come from browser extensions, not from app/business logic.
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('unhandledrejection', (event) => {
    const reasonText =
      typeof event.reason === 'string'
        ? event.reason
        : (event.reason?.message || '');

    if (
      reasonText.includes('message channel closed before a response was received') ||
      reasonText.includes('Could not establish connection. Receiving end does not exist')
    ) {
      event.preventDefault();
    }
  });
}

// Pre-AuthGate router - minimal path matcher so the public chart route
// doesn't get gated behind login. Only one path needs this today; if more
// follow, swap for react-router-dom.
function isPublicChartPath(): boolean {
  if (typeof window === 'undefined') return false;
  return /^\/public\/chart\//i.test(window.location.pathname);
}

function isAuthActionCompletePath(): boolean {
  if (typeof window === 'undefined') return false;
  return /^\/auth\/action-complete\/?$/i.test(window.location.pathname);
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

if (isPublicChartPath()) {
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <PublicChart />
        </Suspense>
      </HelmetProvider>
    </React.StrictMode>
  );
} else if (isAuthActionCompletePath()) {
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <AuthActionCompletePage />
        </Suspense>
      </HelmetProvider>
    </React.StrictMode>
  );
} else {
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <AuthProvider>
          <AuthGate>
            <App />
          </AuthGate>
        </AuthProvider>
      </Provider>
    </React.StrictMode>
  );
}
